/**
 * Provider User Management Component
 * 
 * Manages provider users (platform administrators, engineers, support staff)
 * Only accessible to provider_admin role
 * 
 * Features:
 * - List all provider users with pagination
 * - Search by name or email
 * - Filter by role and status
 * - Create new provider users
 * - Edit existing provider users
 * - Enable/disable users
 * - Change user roles
 * - Delete users
 * - Resend invitation emails
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { EnhancedTable, type TableColumn, type TableAction } from './ui';
import { AppIconButton, AppListPage } from './design-system';
import { useAppToolbar } from '../contexts/AppToolbarContext';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
  PersonOff as DisableIcon,
} from '@mui/icons-material';
import { providerUsersService } from '../services/ProviderUsersService';
import type { ProviderUser, GetProviderUsersRequest } from '../../../shared/src/schemas';
import * as dt from '../../../shared/src/data.types';
import CreateProviderUserDialog from './provider/CreateProviderUserDialog';
import EditProviderUserDialog from './provider/EditProviderUserDialog';
import { useAuth } from '../context/AppContext';
import SensitiveActionDialog, {
  type SensitiveActionType,
} from './dialogs/SensitiveActionDialog';

// Transform ProviderUser for EnhancedTable (id as string)
type ProviderUserDisplay = Omit<ProviderUser, 'id'> & { id: string };

const ProviderUsers: React.FC = () => {
  const { user: currentUser } = useAuth();
  
  // State management
  const [users, setUsers] = useState<ProviderUserDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<Record<string, unknown>>({});
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ProviderUserDisplay | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info' as 'success' | 'error' | 'warning' | 'info',
  });
  const [sensitiveAction, setSensitiveAction] = useState<SensitiveActionType | null>(null);
  const [sensitiveActionUser, setSensitiveActionUser] = useState<ProviderUserDisplay | null>(null);

  // Load users data
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params: GetProviderUsersRequest = {
        page, // Backend uses 1-based pagination
        limit: pageSize,
      };

      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      if (filters.role) {
        params.role = filters.role as GetProviderUsersRequest['role'];
      }

      if (filters.status) {
        params.isActive = filters.status === 'active';
      }

      const usersData = await providerUsersService.listProviderUsers(params);
      // Transform data for EnhancedTable (convert id to string for compatibility)
      const transformedUsers = usersData.map(user => ({
        ...user,
        id: user.id.toString(),
      }));
      setUsers(transformedUsers);
    } catch (error) {
      showSnackbar(
        'Failed to load provider users: ' + (error instanceof Error ? error.message : 'Unknown error'),
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQuery, filters]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info'
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  // Action handlers
  const handleEdit = (user: ProviderUserDisplay) => {
    setSelectedUser(user);
    setEditDialogOpen(true);
  };

  const handleEnableDisable = (user: ProviderUserDisplay) => {
    setSensitiveAction(user.isActive ? 'disable-user' : 'enable-user');
    setSensitiveActionUser(user);
  };

  const handleDelete = (user: ProviderUserDisplay) => {
    setSensitiveAction('delete-user');
    setSensitiveActionUser(user);
  };

  const handleResendInvitationClick = (user: ProviderUserDisplay) => {
    setSensitiveAction('resend-invitation');
    setSensitiveActionUser(user);
  };

  const handleSensitiveActionConfirm = async (stepUpToken: string) => {
    if (!sensitiveActionUser || !sensitiveAction) {
      return;
    }

    const userId = parseInt(sensitiveActionUser.id) as dt.IntegerId;

    try {
      switch (sensitiveAction) {
        case 'resend-invitation': {
          const result = await providerUsersService.resendProviderUserInvitation(userId, stepUpToken);
          showSnackbar(
            `Invitation email resent to ${result.email}. A new temporary password was emailed to the user.`,
            'success',
          );
          break;
        }
        case 'enable-user':
          await providerUsersService.changeProviderUserStatus(userId, true, stepUpToken);
          showSnackbar('User enabled successfully!', 'success');
          break;
        case 'disable-user':
          await providerUsersService.changeProviderUserStatus(userId, false, stepUpToken);
          showSnackbar('User disabled successfully!', 'success');
          break;
        case 'delete-user':
          await providerUsersService.deleteProviderUser(userId, stepUpToken);
          showSnackbar('User deleted successfully!', 'success');
          break;
        default:
          throw new Error('Unsupported action');
      }
      loadUsers();
    } catch (error) {
      showSnackbar(
        error instanceof Error ? error.message : 'Action failed. Please try again.',
        'error',
      );
      throw error;
    }
  };

  const closeSensitiveAction = () => {
    setSensitiveAction(null);
    setSensitiveActionUser(null);
  };

  // Check if resend invitation should be enabled
  // Only allow resending if user hasn't logged in yet (no lastLoginAt)
  const canResendInvitation = (user: ProviderUserDisplay): boolean => {
    return !user.lastLoginAt && user.isActive;
  };

  // Check if current user can enable/disable another user
  // Rules:
  // - Cannot disable yourself
  // - Admin can enable/disable anyone (admin, engineer, support)
  // - Engineer can only enable/disable support
  // - Support cannot enable/disable anyone
  const canEnableDisableUser = (targetUser: ProviderUserDisplay): boolean => {
    if (!currentUser) return false;
    
    // Cannot disable yourself
    if (currentUser.id === parseInt(targetUser.id)) {
      return false;
    }

    const currentRole = currentUser.role;
    const targetRole = targetUser.role;

    // Admin can enable/disable anyone
    if (currentRole === dt.ALL_USER_ROLES.provider_admin) {
      return true;
    }

    // Engineer can only enable/disable support
    if (currentRole === dt.ALL_USER_ROLES.provider_engineer) {
      return targetRole === dt.ALL_USER_ROLES.provider_support;
    }

    // Support cannot enable/disable anyone
    return false;
  };

  // Check if current user can delete another user
  // Same rules as enable/disable
  const canDeleteUser = (targetUser: ProviderUserDisplay): boolean => {
    return canEnableDisableUser(targetUser);
  };

  // Check if current user can edit another user
  // Same rules as enable/disable
  const canEditUser = (targetUser: ProviderUserDisplay): boolean => {
    return canEnableDisableUser(targetUser);
  };

  // Check if current user can change another user's role
  // Rules:
  // - Cannot change your own role
  // - Only Admin can change roles
  // - Engineer and Support cannot change roles
  const canChangeUserRole = (targetUser: ProviderUserDisplay): boolean => {
    if (!currentUser) return false;
    
    // Cannot change your own role
    if (currentUser.id === parseInt(targetUser.id)) {
      return false;
    }

    const currentRole = currentUser.role as string;

    // Only admin can change roles
    return currentRole === dt.ALL_USER_ROLES.provider_admin;
  };

  // Get role display name and color
  const getRoleInfo = (role: string) => {
    switch (role) {
      case dt.ALL_USER_ROLES.provider_admin:
        return { label: 'Admin', color: 'error' as const };
      case dt.ALL_USER_ROLES.provider_engineer:
        return { label: 'Engineer', color: 'primary' as const };
      case dt.ALL_USER_ROLES.provider_support:
        return { label: 'Support', color: 'secondary' as const };
      case dt.ALL_USER_ROLES.provider_deployment_engineer:
        return { label: 'Deployment', color: 'info' as const };
      default:
        return { label: role, color: 'default' as const };
    }
  };

  // EnhancedTable columns
  const columns: TableColumn<ProviderUserDisplay>[] = [
    {
      field: 'firstName',
      headerName: 'First name',
      flex: 1,
      minWidth: 140,
      renderCell: (row: ProviderUserDisplay) => (
        <Typography variant="body2" fontWeight="medium">
          {row.firstName}
        </Typography>
      ),
    },
    {
      field: 'lastName',
      headerName: 'Last name',
      flex: 1,
      minWidth: 140,
      renderCell: (row: ProviderUserDisplay) => (
        <Typography variant="body2" fontWeight="medium">
          {row.lastName}
        </Typography>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.5,
      minWidth: 220,
      renderCell: (row: ProviderUserDisplay) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <EmailIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 16 }} />
          <Typography variant="body2" color="text.secondary">
            {row.email}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 130,
      align: 'center',
      renderCell: (row: ProviderUserDisplay) => {
        const roleInfo = getRoleInfo(row.role);
        return <Chip label={roleInfo.label} size="small" color={roleInfo.color} variant="filled" />;
      },
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 110,
      align: 'center',
      renderCell: (row: ProviderUserDisplay) => (
        <Chip
          label={row.isActive ? 'Active' : 'Inactive'}
          size="small"
          color={row.isActive ? 'success' : 'default'}
          variant="filled"
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 110,
      align: 'center',
      valueFormatter: (value) => {
        const date = new Date(value as string);
        return date.toLocaleDateString();
      },
    },
    {
      field: 'lastLoginAt',
      headerName: 'Last Login',
      width: 110,
      align: 'center',
      valueFormatter: (value) => {
        if (!value) return 'Never';
        const date = new Date(value as string);
        return date.toLocaleDateString();
      },
    },
  ];

  // Table actions
  const actions: TableAction<ProviderUserDisplay>[] = [
    {
      label: 'Edit',
      icon: <EditIcon />,
      onClick: (row: ProviderUserDisplay) => handleEdit(row),
      disabled: (row: ProviderUserDisplay) => !canEditUser(row),
      color: 'primary',
    },
    {
      label: 'Enable/Disable',
      description: (row: ProviderUserDisplay) =>
        row.isActive
          ? 'Disable this user — they will lose access until re-enabled'
          : 'Re-enable this user so they can sign in again',
      icon: <DisableIcon />,
      onClick: (row: ProviderUserDisplay) => handleEnableDisable(row),
      disabled: (row: ProviderUserDisplay) => !canEnableDisableUser(row),
      color: 'warning',
    },
    {
      label: 'Resend Invitation',
      description:
        'Generate a new temporary password and resend the welcome email (only for users who have never logged in)',
      icon: <EmailIcon />,
      onClick: (row: ProviderUserDisplay) => handleResendInvitationClick(row),
      disabled: (row: ProviderUserDisplay) => !canResendInvitation(row),
      color: 'info',
    },
    {
      label: 'Delete',
      description: 'Permanently remove this provider user',
      icon: <DeleteIcon />,
      onClick: (row: ProviderUserDisplay) => handleDelete(row),
      disabled: (row: ProviderUserDisplay) => !canDeleteUser(row),
      color: 'error',
    },
  ];

  const toolbarPageActions = useMemo(
    () => (
      <Tooltip title="Refresh">
        <AppIconButton onClick={loadUsers} color="primary" aria-label="Refresh users">
          <RefreshIcon />
        </AppIconButton>
      </Tooltip>
    ),
    [loadUsers],
  );

  useAppToolbar({ pageActions: toolbarPageActions });

  if (loading && users.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <AppListPage>
      <EnhancedTable<ProviderUserDisplay>
            data={users}
            totalCount={users.length}
            loading={loading}
            columns={columns}
            actions={actions}
            page={page}
            pageSize={pageSize}
            pageSizeOptions={[5, 10, 25, 50]}
            onPageChange={setPage}
            onPageSizeChange={(size: number) => {
              setPageSize(size);
              setPage(1);
            }}
            searchable
            searchTerm={searchQuery}
            searchPlaceholder="Search by name or email..."
            onSearchChange={setSearchQuery}
            onAdd={() => setCreateDialogOpen(true)}
            addLabel="Create provider user"
            filters={filters}
            filterOptions={[
              {
                field: 'role',
                label: 'Role',
                type: 'select',
                options: [
                  { label: 'Admin', value: dt.ALL_USER_ROLES.provider_admin },
                  { label: 'Engineer', value: dt.ALL_USER_ROLES.provider_engineer },
                  { label: 'Support', value: dt.ALL_USER_ROLES.provider_support },
                  { label: 'Deployment', value: dt.ALL_USER_ROLES.provider_deployment_engineer },
                ],
              },
              {
                field: 'status',
                label: 'Status',
                type: 'select',
                options: [
                  { label: 'Active', value: 'active' },
                  { label: 'Inactive', value: 'inactive' },
                ],
              },
            ]}
            onFilterChange={setFilters}
            emptyStateTitle="No users found"
            emptyStateDescription="Try adjusting your search or filters"
            rowHeight={56}
            maxHeight="100%"
          />

      {/* Snackbar for notifications */}
      {snackbar.open && (
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 9999,
          }}
        >
          {snackbar.message}
        </Alert>
      )}

      {/* Create Provider User Dialog */}
      <SensitiveActionDialog
        open={sensitiveAction !== null && sensitiveActionUser !== null}
        action={sensitiveAction}
        target={
          sensitiveActionUser
            ? {
                id: sensitiveActionUser.id,
                email: sensitiveActionUser.email,
                displayName: `${sensitiveActionUser.firstName} ${sensitiveActionUser.lastName}`,
              }
            : null
        }
        onClose={closeSensitiveAction}
        onConfirm={handleSensitiveActionConfirm}
      />

      <CreateProviderUserDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={() => {
          setCreateDialogOpen(false);
          loadUsers();
        }}
      />

      {/* Edit Provider User Dialog */}
      <EditProviderUserDialog
        open={editDialogOpen}
        user={selectedUser}
        canChangeRole={selectedUser ? canChangeUserRole(selectedUser) : false}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedUser(null);
        }}
        onSuccess={() => {
          showSnackbar('User updated successfully!', 'success');
          loadUsers();
        }}
      />
    </AppListPage>
  );
};

export default ProviderUsers;
